import { useState, useEffect, useCallback } from 'react'
import { getPeriodDates as getPeriodDatesLib, isInvalidDateRange } from '../../shared/lib/time'
import { exportDiagnostics } from '../../shared/lib/exportDiagnostics'
import { matchesOrderSearch, summarizeOrders } from './searchStats'

export function useOrderListData(token) {
    const [activeOrders, setActiveOrders] = useState([])
    const [historyOrders, setHistoryOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [period, setPeriod] = useState('week')
    const [filterFrom, setFilterFrom] = useState('')
    const [filterTo, setFilterTo] = useState('')
    const [search, setSearch] = useState('')
    const [activeStatus, setActiveStatus] = useState('all')
    const [exporting, setExporting] = useState(false)

    const loadActive = useCallback(() => {
        if (period === 'custom' && isInvalidDateRange(filterFrom, filterTo)) return Promise.resolve()
        const { from, to } = getPeriodDatesLib(period, filterFrom, filterTo)
        const params = new URLSearchParams({ scope: 'active' })
        if (from) params.set('from', from)
        if (to) params.set('to', to)
        return fetch(`/api/orders?${params}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setActiveOrders(data) })
    }, [token, period, filterFrom, filterTo])

    const loadHistory = useCallback(() => {
        if (period === 'custom' && isInvalidDateRange(filterFrom, filterTo)) return Promise.resolve()
        let from, to

        if (period !== 'custom') {
            const dates = getPeriodDatesLib(period, filterFrom, filterTo)
            from = dates.from
            to = dates.to
        } else {
            from = filterFrom || null
            to = filterTo || null
        }

        const params = []
        if (from) params.push(`from=${from}`)
        if (to) params.push(`to=${to}`)

        return fetch(`/api/orders?${params.join('&')}`, { headers: { Authorization: `Bearer ${token}` } })
            .then(res => res.json())
            .then(data => { if (Array.isArray(data)) setHistoryOrders(data) })
    }, [token, period, filterFrom, filterTo])

    useEffect(() => {
        setLoading(true)
        Promise.all([loadActive(), loadHistory()]).then(() => setLoading(false))
    }, [loadActive, loadHistory])

    useEffect(() => {
        if (!token) return
        let socket
        let reconnectTimeout

        const connect = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
            socket = new WebSocket(`${protocol}://${window.location.host}/api/ws?token=${token}`)

            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type !== 'orders_changed') return
                    loadActive()
                    if (data.reason === 'completed') loadHistory()
                } catch { }
            }

            socket.onclose = () => {
                reconnectTimeout = setTimeout(connect, 5000)
            }
        }

        connect()

        return () => {
            clearTimeout(reconnectTimeout)
            if (socket) socket.close()
        }
    }, [token, loadActive, loadHistory])

    const matchesSearch = useCallback((o) => {
        return matchesOrderSearch(o, search, ['car_brand', 'car_model', 'plate', 'client_name', 'client_phone', 'mechanic_name', 'number', 'vin'])
    }, [search])

    const filteredActive = activeOrders.filter(matchesSearch)
    const filteredHistory = historyOrders.filter(matchesSearch)
    const matchingStats = summarizeOrders([...filteredActive, ...filteredHistory])

    const counts = {
        all: matchingStats.total,
        open: matchingStats.open,
        in_progress: matchingStats.inProgress - matchingStats.open,
        done: matchingStats.sent + matchingStats.closedNotSent,
        doneSent: matchingStats.sent,
        doneNotSent: matchingStats.closedNotSent,
        expired: matchingStats.expired,
        expiredOpen: matchingStats.expiredOpen,
        expiredProgress: matchingStats.expiredProgress,
    }

    const displayedActive = filteredActive.filter(o => {
        if (activeStatus === 'all') return true
        if (activeStatus === 'open') return o.status === 'open'
        if (activeStatus === 'in_progress') return o.status === 'in_progress'
        return false
    })

    const displayedHistory = filteredHistory.filter(o => {
        if (activeStatus === 'all') return true
        if (activeStatus === 'done') return o.outcome === 'approved'
        if (activeStatus === 'expired') return o.outcome === 'expired_open' || o.outcome === 'expired_progress'
        return false
    })

    const exportXlsx = async () => {
        setExporting(true)
        try {
            await exportDiagnostics({ orders: filteredHistory, token, filters: {
                period,
                from: filterFrom,
                to: filterTo,
                status: activeStatus,
                search,
            } })
        } catch (e) { console.error(e) }
        setExporting(false)
    }

    return {
        loading,
        period, setPeriod,
        filterFrom, setFilterFrom,
        filterTo, setFilterTo,
        search, setSearch,
        activeStatus, setActiveStatus,
        exporting,
        counts,
        displayedActive,
        displayedHistory,
        hasHistoryToExport: filteredHistory.length > 0,
        exportXlsx,
    }
}
